<template>
    <div class="sleep-analysis">
        <div class="sleep-table-wrapper">
            <table class="sleep-table" border>
                <thead>
                    <tr>
                        <th> </th>
                        <th v-for="daytime in $options.TimeSlot"
                            :key="daytime.slot"
                            class="time-slot">
                            <div>
                                <span>{{ daytime.display }}</span>
                                <span v-if="daytime.slot === 23" class="day-end">12 AM</span>
                            </div>
                        </th>
                        <th>
                            Sleep hours
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(dayData, dayIndex) in sleepData"
                        :key="dayIndex">
                        <td class="sleep-date">
                            <div class="borderise">
                                {{ formatDate(dayData.day) }}
                            </div>
                            
                            <span class="sleep-line">
                            </span>
                        </td>
                        <td v-for="(daySlot, slotIndex) in dayData.daySlots"
                            :key="slotIndex"
                            :class="['time-slot', {last: slotIndex === dayData.daySlots.length - 1, even: dayIndex % 2}]">
                            <div :class="['borderise', {asleep: sleepStatusArr[dayIndex][slotIndex]}]">
                                <SleepCell v-if="daySlot.sleepDetail.length > 0 || daySlot.sleepStart.length > 0 || daySlot.sleepEnd.length > 0"
                                    :sleep-detail="daySlot.sleepDetail"
                                    :sleep-start="daySlot.sleepStart"
                                    :sleep-end="daySlot.sleepEnd"
                                    :day-slot="slotIndex"
                                    :sleep-status="getSleepStatusArr(dayIndex, slotIndex)">
                                </SleepCell>
                                <span v-else>
                                    &nbsp;
                                </span>
                            </div>
                        </td>
                        <td :class="['sleep-hours', {'line-keep': getSleepStatusArr(dayIndex, 23).current}]">
                            <template v-if="dayData.sleepHours">
                                {{ dayData.sleepHours }} hrs
                            </template>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
import format from '@/module/util/format'

import { getTimeFromIndex, getHrMnForDay, getLast30Days } from './util'

let Last30Days = getLast30Days()

import SleepCell from './SleepCell'

/**
 * A single Sleep hourly Object
 * that contains information about sleep start, end and 
 * all factors that effected it
 * @typedef {Object} sleepSlot 
 * @property {number[]} sleepStart
 * @property {number[]} sleepEnd
 * @property {string[]} sleepDetail
 */

/**
 * A single day slot containing sleep info for every 
 * hourly slot in the day
 * @typedef {sleepSlot[]} daySlot
 * 
 */

/**
 * Defines Array of time slots between 12AM - 11PM `[{slot: 0, display: '12 AM'} ... {slot: 23, display: '11 PM'}]`
 */
const TimeSlot = [...new Array(24).keys()].map((key) => ({
        slot: key,
        display: getTimeFromIndex(key)
    }))


export default {
    name: 'sleep-analysis',
    
    props: {
        /**
         * List of events for sleep
         */
        eventList: {
            type: Array,
            required: true
        }
    },

    created() {
        this.$options.TimeSlot = TimeSlot
        this.$options.Last30Days = Last30Days
    },

    computed: {
        /**
         * Calculates the sleep data to plot on table using `eventList`
         */
        sleepData() {
            try {
                /**
                 * One day time in milliseconds
                 */
                const OneDay = 24 * 60 * 60 * 1000
                // Taking last day for sleep calculations
                let lastDay = Last30Days[Last30Days.length - 1]
                // Filtering events for only Last 30 days
                let filterEvents = this.eventList.filter((event) => event.sleep_start > lastDay.getTime())
                // Create an array from `Last30Days` to include sleep details from each day
                return Last30Days.map((day, dayIndex) => {

                    // Calculating start and end time for the `day`
                    const startTime = day.getTime()
                    const endTime = new Date(day).setHours(23, 59, 59, 999)

                    // Filtering events for the current `day` where sleep started
                    const sleepStartEvents = filterEvents.filter(({ sleep_start }) => sleep_start >= startTime && sleep_start <= endTime)

                    // Filtering events for the current `day` where sleep ended
                    const sleepEndEvents = filterEvents.filter(({ sleep_end }) => sleep_end >= startTime && sleep_end <= endTime)

                    // Filtering events for current `day` where factors are valid
                    const factorEvents = filterEvents.filter(({ time }) => time - OneDay >= startTime && time - OneDay <= endTime)

                    // 
                    /**
                     * Creating an empty array of Object from for all time slots in a day
                     * @type {daySlot}
                     */
                    let emptyDaySlots = TimeSlot.map((slot) => ({
                        // Will contain factors that effected sleep
                        sleepDetail: [],
                        sleepStart: [],
                        sleepEnd: []
                    }))

                    // Get sleep start Slots from events of the day
                    let daySlots = sleepStartEvents.reduce(this.getSleepStartFromEvents, emptyDaySlots)
                    // Get sleep end slots from events of the day
                    daySlots = sleepEndEvents.reduce(this.getSleepEndFromEvents, daySlots)
                    // Get  Day Slot Factors information (caffeine, nap, etc) from events of the day
                    daySlots = factorEvents.reduce(this.getDayFactorsFromEvents, daySlots)
                    // Get total sleep time of a day
                    let sleepTime = sleepStartEvents.reduce((sleepTimeAcc, event) => sleepTimeAcc + event.sleep_end - event.sleep_start, 0)
                    
                    // Getting no. of hours and minutes from the sleepTime assuming maximum of 24 hours
                    //console.log(sleepTime, sleepStartEvents)
                    const sleepHours = getHrMnForDay(sleepTime)
                    return {
                        daySlots,
                        day,
                        sleepHours
                    }
                })
            } catch (error) {
                console.error(error)
                return []
            }
        },
        sleepStatusArr() {
            try {
                let sleepCount = 0
                return this.sleepData.slice().reverse().map((dayData, dayIndex) => {
                    return dayData.daySlots.map((daySlot, slotIndex) => {
                        sleepCount += daySlot.sleepStart.length
                        const sleepStatus = sleepCount > 0
                        sleepCount -= daySlot.sleepEnd.length
                        return sleepStatus
                    })
                }).reverse()
            } catch (error) {
                console.error(error)
                return [...Array(30)].map(e => Array(24))
            }
        }
    },

    methods: {
        ...format,
        /**
         *  Adds and sleep starting time on `daySlotAcc[slot].sleepStart` for the `event`
         * @param {daySlot} daySlotAcc
         * @param {Object} event
         */
        getSleepStartFromEvents(daySlotAcc, event) {
            const sleepStartIndex = new Date(event.sleep_start).getHours()
            daySlotAcc[sleepStartIndex].sleepStart.push(event.sleep_start)
            return daySlotAcc
        },
        /**
         * Adds sleep end time to `daySlotAcc[slot].sleepEnd` for the `event`
         * @param {daySlot} daySlotAcc
         * @param {Object} event
         */
        getSleepEndFromEvents(daySlotAcc, event) {
            const sleepEndIndex = new Date(event.sleep_end).getHours()
            daySlotAcc[sleepEndIndex].sleepEnd.push(event.sleep_end)
            return daySlotAcc
        },
        /**
         * Adds factors affecting sleep on `daySlotAcc[slot].sleepDetail` array 
         * for the event
         * @param {daySlot} daySlotAcc
         * @param {Object} event
         */
        getDayFactorsFromEvents(daySlotAcc, event) {
            if(event.time_detail)
                Object.entries(event.time_detail).forEach(([key, detailArr]) => {
                    detailArr.forEach((detail) => {
                        let slotKey = detail.start / 60
                        if(!daySlotAcc[slotKey].sleepDetail.includes(key))
                            daySlotAcc[slotKey].sleepDetail.push(key)
                    })
                })
            return daySlotAcc
        },
        /**
         * Give the sleep status array of before, current and one hour after 
         * of the current Slot
         */
        getSleepStatusArr(dayIndex, slotIndex) {
            let current = this.sleepStatusArr[dayIndex][slotIndex], before, after
            if(slotIndex === 0) {
                before = dayIndex == 0 ? false : this.sleepStatusArr[dayIndex - 1][23]
                after = this.sleepStatusArr[dayIndex][slotIndex + 1]
            }
            else if(slotIndex === 23) {
                before = this.sleepStatusArr[dayIndex][slotIndex - 1]
                after = dayIndex === 29 ? false : this.sleepStatusArr[dayIndex + 1][0]
            }
            else {
                before = this.sleepStatusArr[dayIndex][slotIndex - 1]
                after = this.sleepStatusArr[dayIndex][slotIndex + 1]
            }
            return {
                current,
                before,
                after
            }
        }
    },

    components: {
        SleepCell
    }
}
</script>

<style lang="scss" scoped>
@import "@/module/app/theme/color.scss";
.sleep-analysis {
    background-color: #d2e8b7;
    padding: 12px;
}
.sleep-table-wrapper {
    background-color: $white-color;
    padding: 0 10px 13px 10px;
}
table.sleep-table {
    border-collapse: collapse;
    border: none;
    width: 100%;
    min-width: 900px;
    
    table-layout: fixed;
    tr {
        transform: scale(1);
    }
    thead th {
        font-size: 10px;
        color: $grey-500;
        font-weight: 500;
        border: none;
        padding: 5px;
    }
    tbody tr:first-child td.time-slot{
        border-top: 1px solid $grey-100;
    }
     tbody tr:first-child td.sleep-date .borderise {
         border-top: 1px solid $grey-100;
         margin-top: -1px;
     }
    th:last-child {
        width: 80px;
        text-align: left;
        padding-left: 20px;
    }
    th:first-child {
        width: 80px;
    }
    th.time-slot {
        overflow: initial;
        padding: 4px;
        line-height: initial;
        > div {
            line-height: initial;
            position: relative;
            overflow: initial;
            width: 100%;
            > span {
                position: absolute;
                left: -15px;
            }
            > span.day-end {
                left: unset;
                right: -10px;
                z-index: 1;
            }
        }
        > div::before {
            content: 's';
            opacity: 0;
        }
    }
    td.time-slot {
        padding: 0;
    }
    td.time-slot.last{
        .borderise {
            border-right: 1px solid $grey-000;
        }
    }
    td {
        font-size: 10px;
        line-height: 12px;
        padding: 3px 5px;
        color: $grey-500;
        border: none;
        height: 16px;
        background-color: $white-color;
    }
    td.even {
        background-color: $grey-000;
    }
    td > div.borderise {
        border-left: 0.5px solid;
        border-bottom: 0.5px solid;
        border-color: $grey-100;
        height: 100%;
        background-color: inherit;
        position: relative;
        z-index: 1;
        &.asleep {
            z-index: inherit;
        }
    }
    td.sleep-date {
        position: static;
        z-index: 1;
        height: 16px;
        line-height: 16px;
        padding: 0;
    }
    td.sleep-date > div.borderise {
        padding-left: 5px;
    }
    td span.sleep-line {
        position: absolute;
        top: 8px;
        left: 80px;
        right: 60px;
        border-top: 1px dashed $vital-color;
        z-index: 1;
    }
    td.sleep-hours {
        background-color: $white-color;
        font-weight: bold;
        color: $black-color;
        border: none;
        z-index: 1;
        padding-left: 22px;
        &.line-keep {
            z-index: unset;
        }
    }
}
</style>
